import { maxDispensingFee, minDispensingFee } from './api.js';
import { fmtR, dosageUnit, capitalise, esc, escAttr } from './utils.js';

export function schemeRow(scheme, entry) {
  if (!entry) return '';
  const note = entry.price_adjusted
    ? `<span class="medicine-card__scheme-note"> (adjusted for your pack size)</span>`
    : '';
  return `
      <div class="medicine-card__scheme-row">
        <span class="medicine-card__scheme-name">${esc(scheme)}</span>
        <span class="medicine-card__scheme-price">${fmtR(entry.price)}${note}</span>
      </div>`;
}

export function renderCard(med) {
  const sepNum  = parseFloat(String(med.sep).replace(/[R\s,]/g, '')) || 0;
  const maxDisp = maxDispensingFee(sepNum);
  const minDisp = minDispensingFee(sepNum);
  const sepStr  = typeof med.sep === 'string' ? med.sep.trim() : fmtR(sepNum);

  const ingredients = med.ingredients || [];
  const ingPayload  = JSON.stringify(ingredients);

  const schedule = med.schedule || '';
  const isGeneric = med.is_generic === true || med.is_generic === 'Generic';

  const badges = [
    isGeneric 
      ? `<span class="medicine-card__badge medicine-card__badge--generic">Generic</span>` 
      : `<span class="medicine-card__badge medicine-card__badge--original">Original</span>`,
    schedule ? `<span class="medicine-card__badge medicine-card__badge--schedule">${esc(schedule)}</span>` : ''
  ].filter(Boolean).join('');

  const unit    = dosageUnit(med.dosage_form);
  const form    = med.dosage_form ? capitalise(med.dosage_form) : '';
  const packNum = med.pack_size != null ? Math.round(med.pack_size) : null;
  const packStr = packNum != null ? `${packNum}${unit ? '\u202f' + unit : ''}` : '';

  const sp          = med.scheme_prices || {};
  const bontasEntry = sp.bonitas || null;
  const polmedEntry = sp.polmed  || null;
  const gemsEntry   = sp.gems    || null;
  const hasSchemes  = bontasEntry || polmedEntry || gemsEntry;

  const schemesBlock = hasSchemes ? `
  <div class="medicine-card__divider"></div>
  <div class="medicine-card__schemes">
    <span class="medicine-card__schemes-label">Medical Aid Covered Price</span>
    ${schemeRow('Bonitas', bontasEntry)}
    ${schemeRow('POLMED', polmedEntry)}
    ${schemeRow('GEMS', gemsEntry)}
  </div>` : '';

  return `
<article class="medicine-card" role="button" tabindex="0" aria-label="View ingredients for ${escAttr(med.name || 'this medicine')}" data-ingredients='${escAttr(ingPayload)}' data-name="${escAttr(med.name || '')}" data-form="${escAttr(med.dosage_form || '')}">
  <div class="medicine-card__header">
    <h3 class="medicine-card__name">${esc(med.name || 'Unknown')}</h3>
    ${badges ? `<div class="medicine-card__badges">${badges}</div>` : ''}
  </div>
  ${(form || packStr) ? `
  <div class="medicine-card__pack-row">
    ${form    ? `<span class="medicine-card__pack-form">${esc(form)}</span>` : ''}
    ${packStr ? `<span class="medicine-card__pack-size">${esc(packStr)}</span>` : ''}
  </div>` : ''}
  <div class="medicine-card__divider"></div>
  <div class="medicine-card__prices">
    <span class="medicine-card__sep-label">Official Price (SEP)</span>
    <div class="medicine-card__sep">${esc(sepStr)}</div>
    <div class="medicine-card__disp-row">
      <div class="medicine-card__disp medicine-card__disp--min">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><polyline points="18 15 12 9 6 15"/></svg>
        <span class="medicine-card__disp-label">Lowest fee</span>
        <span class="medicine-card__disp-val">${fmtR(minDisp)}</span>
      </div>
      <div class="medicine-card__disp-divider" aria-hidden="true"></div>
      <div class="medicine-card__disp medicine-card__disp--max">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
        <span class="medicine-card__disp-label">Highest fee</span>
        <span class="medicine-card__disp-val">${fmtR(maxDisp)}</span>
      </div>
    </div>
  </div>${schemesBlock}
  <div class="medicine-card__footer">
    <button type="button" class="medicine-card__ingredients-btn">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg>
      View ingredients
    </button>
  </div>
</article>`;
}

export function showIngredientsModal(name, ingJson) {
  document.getElementById('ingredients-modal')?.remove();

  let ingredients = [];
  try { ingredients = JSON.parse(ingJson); } catch { ingredients = []; }

  const rows = ingredients.length
    ? ingredients.map(i => `
        <div class="ingredient-row">
          <span class="ingredient-row__name">${esc(i.name || 'Unknown')}</span>
          ${(i.strength || i.unit)
            ? `<span class="ingredient-row__dose">${esc([i.strength, i.unit].filter(Boolean).join(' '))}</span>`
            : ''}
        </div>`).join('')
    : `<div class="ingredient-empty">No ingredients listed.</div>`;

  const modal = document.createElement('div');
  modal.id = 'ingredients-modal';
  modal.className = 'ingredients-modal';
  modal.innerHTML = `
    <div class="ingredients-modal__backdrop"></div>
    <div class="ingredients-modal__box" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <span class="ingredients-modal__handle" aria-hidden="true"></span>
      <div class="ingredients-modal__header">
        <h2 class="ingredients-modal__title" id="modal-title">Ingredients</h2>
        <button type="button" class="ingredients-modal__close" aria-label="Close">&times;</button>
      </div>
      <p class="ingredients-modal__medname">${esc(name)}</p>
      <div class="ingredients-modal__list">${rows}</div>
    </div>`;

  const close = () => {
    modal.style.animation = 'fadeIn 0.2s ease reverse';
    setTimeout(() => modal.remove(), 180);
  };
  modal.querySelector('.ingredients-modal__backdrop').addEventListener('click', close);
  modal.querySelector('.ingredients-modal__close').addEventListener('click', close);
  document.addEventListener('keydown', function handler(e) {
    if (e.key === 'Escape') { close(); document.removeEventListener('keydown', handler); }
  });
  document.body.appendChild(modal);
  modal.querySelector('.ingredients-modal__close').focus();
}

export function showSection(id) {
  ['hero','results','not-found'].forEach(s => {
    const el = document.getElementById(s);
    if (el) el.classList.toggle('section--active', s === id);
  });
}
